import { Badge, Flex, Table, Text } from '@radix-ui/themes'
import cn from 'classnames'
import {
  DATA_COVERAGE_ALERT_WINDOW_DAYS,
  DATA_COVERAGE_MIN_EXPECTED_HOURS,
  DATA_COVERAGE_WINDOWS_DAYS,
  type DataCoverageWindowDays
} from '@willie/schemas'
import { formatElapsed, formatTimeAgo, formatTimestamp } from '~/utils/date'
import { plural } from '~/utils/plural'
import type { StationDetail } from '../types'
import { CoverageDayStrip, STRIP_LEGEND } from './CoverageDayStrip'
import { coverageColor, formatCoverageRatio } from './format'
import type { CoverageWindow, SourceCoverage, StationDataCoverage } from './types'

type StationDataSource = StationDetail['data_sources'][number]

type NotScoredStatus = Exclude<SourceCoverage['status'], 'scored'>

const NOT_SCORED_LABELS: Record<NotScoredStatus, string> = {
  not_applicable: 'Not scored',
  unconfigured: 'Unconfigured: no value path',
  no_data: 'No covered hour yet'
}

const STRIP_DAYS = Math.max(...DATA_COVERAGE_WINDOWS_DAYS)

const isAlertWindow = (days: DataCoverageWindowDays): boolean => days === DATA_COVERAGE_ALERT_WINDOW_DAYS

const alertWindowOf = (windows: CoverageWindow[]): CoverageWindow | undefined =>
  windows.find(({ days }) => isAlertWindow(days))

const openGapSinceOf = (windows: CoverageWindow[]): string | null =>
  windows.find(({ openGapSince }) => openGapSince !== null)?.openGapSince ?? null

// Windows end a settle margin before now, so a gap stays open there for a day after the live read sees data again.
const stillSilentSince = (coverage: SourceCoverage, source: StationDataSource | undefined): string | null => {
  const since = source?.retired_at ? null : openGapSinceOf(coverage.windows)
  return since !== null && coverage.lastDataAt !== null && coverage.lastDataAt > since ? null : since
}

const windowTitle = (window: CoverageWindow): string =>
  window.ratio === null
    ? `${window.expectedHours} h expected — a ratio needs ${DATA_COVERAGE_MIN_EXPECTED_HOURS}`
    : `${window.coveredHours} / ${window.expectedHours} h · ${plural(window.gapCount, 'gap')} · longest ${window.longestGapHours} h`

const NoValue = () => (
  <Text size="1" color="gray">
    —
  </Text>
)

const WindowCells = ({ windows }: { windows: CoverageWindow[] }) =>
  DATA_COVERAGE_WINDOWS_DAYS.map((days) => {
    const window = windows.find((candidate) => candidate.days === days)
    return (
      <Table.Cell key={days} className={cn(isAlertWindow(days) && 'bg-[var(--accent-a2)]')}>
        {window ? (
          <Text
            size="2"
            color={coverageColor(window.ratio)}
            weight={isAlertWindow(days) ? 'bold' : 'regular'}
            title={windowTitle(window)}
          >
            {formatCoverageRatio(window.ratio)}
          </Text>
        ) : (
          <NoValue />
        )}
      </Table.Cell>
    )
  })

const GapsCell = ({ windows }: { windows: CoverageWindow[] }) => {
  const window = alertWindowOf(windows)
  if (!window || window.expectedHours === 0) return <NoValue />
  return (
    <Text size="2" color={window.gapCount === 0 ? 'gray' : undefined}>
      {window.gapCount === 0 ? 'None' : `${window.gapCount} · longest ${window.longestGapHours} h`}
    </Text>
  )
}

const OpenGapBadge = ({ label, since, now }: { label: string; since: string; now: Date }) => (
  <Badge color="red" variant="soft">
    {label} {formatTimestamp(since)} ({formatElapsed(since, now)})
  </Badge>
)

const LastDataCell = ({ lastDataAt, now }: { lastDataAt: string | null; now: Date }) =>
  lastDataAt ? (
    <Text size="2" title={formatTimestamp(lastDataAt)}>
      {formatTimeAgo(lastDataAt, now)}
    </Text>
  ) : (
    <NoValue />
  )

interface StationRowProps {
  windows: CoverageWindow[]
  openGapSince: string | null
  now: Date
}

const StationRow = ({ windows, openGapSince, now }: StationRowProps) => (
  <Table.Row>
    <Table.RowHeaderCell>
      <Flex direction="column" align="start" gap="1">
        <Text size="2" weight="medium">
          Station
        </Text>
        <Text size="1" color="gray">
          Every scored source
        </Text>
        {openGapSince ? <OpenGapBadge label="Gap open since" since={openGapSince} now={now} /> : null}
      </Flex>
    </Table.RowHeaderCell>
    <Table.Cell>
      <NoValue />
    </Table.Cell>
    <WindowCells windows={windows} />
    <Table.Cell>
      <GapsCell windows={windows} />
    </Table.Cell>
    <Table.Cell />
  </Table.Row>
)

interface SourceRowProps {
  coverage: SourceCoverage
  source: StationDataSource | undefined
  openGapSince: string | null
  now: Date
}

const SourceRow = ({ coverage, source, openGapSince, now }: SourceRowProps) => {
  const isRetired = Boolean(source?.retired_at)
  return (
    <Table.Row className={cn(isRetired && 'text-[var(--gray-11)]')}>
      <Table.RowHeaderCell>
        <Flex direction="column" align="start" gap="1">
          <Text size="2">{source?.maker ?? source?.source_type ?? 'Unknown source'}</Text>
          {source?.provider_device_id ? (
            <Text size="1" color="gray" className="font-mono">
              {source.provider_device_id}
            </Text>
          ) : null}
          {source?.retired_at ? (
            <Badge color="amber" variant="soft" title={source.retired_at}>
              Retired · {source.retired_at.slice(0, 10)}
            </Badge>
          ) : null}
          {openGapSince ? <OpenGapBadge label="Silent since" since={openGapSince} now={now} /> : null}
        </Flex>
      </Table.RowHeaderCell>
      <Table.Cell>
        <LastDataCell lastDataAt={coverage.lastDataAt} now={now} />
      </Table.Cell>
      {coverage.status === 'scored' ? (
        <>
          <WindowCells windows={coverage.windows} />
          <Table.Cell>
            <GapsCell windows={coverage.windows} />
          </Table.Cell>
          <Table.Cell>
            <CoverageDayStrip daily={coverage.daily} />
          </Table.Cell>
        </>
      ) : (
        <Table.Cell colSpan={DATA_COVERAGE_WINDOWS_DAYS.length + 2}>
          <Text size="2" color="gray">
            {NOT_SCORED_LABELS[coverage.status]}
          </Text>
        </Table.Cell>
      )}
    </Table.Row>
  )
}

export interface DataCoverageTableProps {
  coverage: StationDataCoverage
  sources: StationDetail['data_sources']
  now?: Date
}

export const DataCoverageTable = ({ coverage, sources, now = new Date() }: DataCoverageTableProps) => {
  const rows = coverage.sources.map((sourceCoverage) => {
    const source = sources.find(({ id }) => id === sourceCoverage.dataSourceId)
    return { coverage: sourceCoverage, source, openGapSince: stillSilentSince(sourceCoverage, source) }
  })
  const stationOpenGapSince = rows.some(({ openGapSince }) => openGapSince !== null)
    ? openGapSinceOf(coverage.station)
    : null

  return (
    <Table.Root variant="surface" size="1">
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell>Source</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Last data</Table.ColumnHeaderCell>
          {DATA_COVERAGE_WINDOWS_DAYS.map((days) => (
            <Table.ColumnHeaderCell key={days} className={cn(isAlertWindow(days) && 'bg-[var(--accent-a3)]')}>
              {days} d
            </Table.ColumnHeaderCell>
          ))}
          <Table.ColumnHeaderCell>Gaps ({DATA_COVERAGE_ALERT_WINDOW_DAYS} d)</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell title={STRIP_LEGEND}>Last {STRIP_DAYS} days</Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        <StationRow windows={coverage.station} openGapSince={stationOpenGapSince} now={now} />
        {rows.map((row) => (
          <SourceRow key={row.coverage.dataSourceId} {...row} now={now} />
        ))}
      </Table.Body>
    </Table.Root>
  )
}
